// frontend/src/pages/ContentLibraryPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContent, getTopics, getBrandVoices } from '../services/api';
import { FileText, Archive, Eye, Loader, Filter, Search, Star } from 'lucide-react';
import api from '../services/api';

const VOICE_COLORS = [
  'bg-indigo-100 text-indigo-700',
  'bg-teal-100 text-teal-700',
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-cyan-100 text-cyan-700',
  'bg-violet-100 text-violet-700',
];

const ContentLibraryPage = () => {
  const [content, setContent] = useState([]);
  const [topics, setTopics] = useState([]);
  const [brandVoices, setBrandVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    topic_id: '',
    content_type: '',
    brand_voice: '',
    search: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [contentRes, topicsRes, voicesRes] = await Promise.all([
        getContent(),
        getTopics(),
        getBrandVoices()
      ]);
      setContent(contentRes.data);
      setTopics(topicsRes.data);
      setBrandVoices(voicesRes.data);
    } catch (err) {
      console.error('Failed to load content', err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Move this content to the Archive?')) return;
    try {
      await api.patch(`/api/content/${id}/archive`);
      setContent(prev => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error('Failed to archive content', err);
    }
  };

  const formatContentType = (type) => {
    const labels = {
      linkedin_post: 'LinkedIn Post',
      white_paper: 'White Paper',
      report: 'Report',
      article: 'Article',
      policy_brief: 'Policy Brief'
    };
    return labels[type] || type;
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    return topic ? topic.name : 'Unknown';
  };

  const getTypeColor = (type) => {
    const colors = {
      linkedin_post: 'bg-blue-100 text-blue-700',
      white_paper: 'bg-purple-100 text-purple-700',
      report: 'bg-green-100 text-green-700',
      article: 'bg-orange-100 text-orange-700',
      policy_brief: 'bg-red-100 text-red-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  // Dynamic — derives label and colour from the loaded brandVoices list
  const getBrandVoiceLabel = (voiceName) => {
    const voice = brandVoices.find(v => v.name === voiceName);
    return voice ? (voice.description || voice.name) : voiceName;
  };

  const getBrandVoiceColor = (voiceName) => {
    const idx = brandVoices.findIndex(v => v.name === voiceName);
    return idx >= 0 ? VOICE_COLORS[idx % VOICE_COLORS.length] : 'bg-gray-100 text-gray-500';
  };

  const filteredContent = content.filter((item) => {
    if (filters.topic_id && item.topic_id !== filters.topic_id) return false;
    if (filters.content_type && item.content_type !== filters.content_type) return false;
    if (filters.brand_voice && item.brand_voice !== filters.brand_voice) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        item.title?.toLowerCase().includes(searchLower) ||
        item.content?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-brand-dark">Content Library</h1>
        <p className="text-gray-500 mt-1">View and manage all generated content</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-500">
            <Filter size={18} />
            <span className="text-sm font-medium">Filters:</span>
          </div>

          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search content..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
            />
          </div>

          <select
            value={filters.topic_id}
            onChange={(e) => setFilters({ ...filters, topic_id: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
          >
            <option value="">All Topics</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>{topic.name}</option>
            ))}
          </select>

          <select
            value={filters.content_type}
            onChange={(e) => setFilters({ ...filters, content_type: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
          >
            <option value="">All Types</option>
            <option value="linkedin_post">LinkedIn Post</option>
            <option value="white_paper">White Paper</option>
            <option value="report">Report</option>
            <option value="article">Article</option>
            <option value="policy_brief">Policy Brief</option>
          </select>

          {/* Dynamic brand voice filter */}
          <select
            value={filters.brand_voice}
            onChange={(e) => setFilters({ ...filters, brand_voice: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
          >
            <option value="">All Voices</option>
            {brandVoices.map((v) => (
              <option key={v.id} value={v.name}>{v.description || v.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content List */}
      {filteredContent.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No content found. Run a research pipeline to generate content.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredContent.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded-full ${getTypeColor(item.content_type)}`}>
                      {formatContentType(item.content_type)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getTopicName(item.topic_id)}
                    </span>
                    <span className="text-xs text-gray-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-xs text-gray-400">
                      {item.content ? item.content.trim().split(/\s+/).length.toLocaleString() : 0} words
                    </span>
                    {item.brand_voice && (
                      <span className={`text-xs px-2 py-1 rounded-full ${getBrandVoiceColor(item.brand_voice)}`}>
                        {getBrandVoiceLabel(item.brand_voice)}
                      </span>
                    )}
                  </div>
                  <h3 className="text-md font-semibold text-gray-800 mb-1">
                    {item.title || 'Untitled'}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2">
                    {item.content?.substring(0, 200)}...
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await api.put(`/api/content/${item.id}/favourite`);
                        setContent(content.map(c => c.id === item.id ? { ...c, is_favourite: res.data.is_favourite } : c));
                      } catch (err) { console.error('Failed to toggle favourite', err); }
                    }}
                    className={`p-2 transition-colors ${item.is_favourite ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-500'}`}
                    title={item.is_favourite ? 'Remove from favourites' : 'Add to favourites'}
                  >
                    <Star size={18} fill={item.is_favourite ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    onClick={() => navigate(`/content/${item.id}`)}
                    className="p-2 text-gray-400 hover:text-brand-mid transition-colors"
                    title="View"
                  >
                    <Eye size={18} />
                  </button>
                  <button
                    onClick={() => handleArchive(item.id)}
                    className="p-2 text-gray-400 hover:text-amber-500 transition-colors"
                    title="Move to Archive"
                  >
                    <Archive size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentLibraryPage;
